<script lang="ts" module>
	const KEY = 'shouter-seen'

	export function seen() {
		try {
			return !!sessionStorage.getItem(KEY)
		} catch {
			return false
		}
	}

	export function mark_seen() {
		try {
			sessionStorage.setItem(KEY, '1')
		} catch {
			return
		}
	}
</script>

<script lang="ts">
	import Button from './button.svelte'
	import { onMount } from 'svelte'
	import { page } from '$app/state'
	import Render from './render.svelte'
	import { track } from '#library/track.ts'

	let dialog: HTMLDialogElement | undefined = $state()

	interface Props {
		pattern: string
	}

	let { pattern }: Props = $props()

	const theme = $derived(page.data.layout.callout_theme ?? 'theme-gimson-on-lupton')
	const title = $derived(page.data.layout.callout_title)
	const content = $derived(page.data.layout.callout_content)
	const cta = $derived(page.data.layout.callout_link)

	onMount(() => {
		mark_seen()

		if (!dialog?.open) return
		const { scrollX, scrollY } = window

		dialog.removeAttribute('open')
		dialog.showModal()
		dialog.focus()

		window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' })
	})
</script>

<dialog
	open
	closedby="any"
	aria-labelledby="shouter-title"
	class="shouter {theme}"
	bind:this={dialog}
	style:--pattern={pattern}
	tabindex="-1"
>
	<form method="dialog">
		<button class="close" aria-label="Close">
			<svg aria-hidden="true">
				<use xlink:href="/images/vector.svg#close"></use>
			</svg>
		</button>
	</form>
	<div class="content">
		<h2 class="title" id="shouter-title">{title}</h2>
		<Render {content} />
		{#if cta}
			<Button
				href={cta.url}
				target={cta.target}
				onclick={() => {
					track('Callout CTA Clicked', { label: cta.text, url: cta.url })
					dialog?.close()
				}}
				icon="arrow-up-right"
				large>{cta.text}</Button
			>
		{/if}
	</div>
</dialog>

<style lang="css">
	.shouter {
		--scale: 0.95;

		position: fixed;
		inset: 0;
		width: fit-content;
		z-index: 100;
		border: none;
		background: var(--c-background);
		color: var(--c-midground);
		box-shadow: 0 0 0 500vw hsl(0 0% 0% / 0.3);
		max-width: var(--s-wrap);
		overflow: clip;
		padding: 0;
		opacity: 0;
		scale: var(--scale);
		outline: none;
		transition:
			opacity 0.2s ease,
			scale 0.2s ease,
			display 0.2s allow-discrete,
			overlay 0.2s allow-discrete;

		@media (--from-mobile) {
			transition-delay: 0.2s;
		}

		@media (prefers-reduced-motion: reduce) {
			--scale: 1;
		}

		:global(html[data-shouter-seen]) & {
			display: none;
		}

		&[open] {
			display: block;
			opacity: 1;
			scale: 1;

			@starting-style {
				opacity: 0;
				scale: var(--scale);
			}
		}

		@media (--until-tablet) {
			margin: 0;
			width: auto;
			height: auto;
			max-height: none;

			&[open] {
				display: grid;
				grid-template-rows: 1fr auto;
			}
		}

		&::after {
			background-image: var(--pattern);
			content: '';
			display: block;
			height: 2rem;

			@media (--until-tablet) {
				height: 5rem;
			}
		}
	}

	.content {
		display: grid;
		gap: 1rem;
		justify-items: start;
		align-content: start;
		overflow: auto;
		overscroll-behavior: contain;
		padding: var(--s-40);
		max-height: calc(100dvh - 6rem);

		@media (--until-tablet) {
			padding: var(--s-80);
			max-height: none;
		}

		:global(ul) {
			display: grid;
			gap: 0.4em;
			font: 600 var(--f-h4);

			@media (--from-tablet) {
				font: 600 var(--f-h6);
			}

			:global(li p) {
				display: contents;
			}

			:global(li strong) {
				all: unset;
				color: var(--c-foreground);
				margin-inline-end: 0.3ch;

				@media (--until-tablet) {
					display: block;
				}
			}
		}
	}

	.content :global(.button) {
		&:hover {
			background-color: var(--c-midground);
			color: var(--c-background);
		}

		&:hover::after {
			opacity: 0;
		}
	}

	.title {
		font: 600 var(--f-h1);
		letter-spacing: var(--ls-h1);
		text-wrap: balance;
		white-space: break-spaces;

		@media (--from-tablet) {
			font: 600 var(--f-h2);
			letter-spacing: var(--ls-h2);
		}
	}

	form {
		display: contents;
	}

	.close {
		position: absolute;
		top: 1.5rem;
		right: 1.5rem;
		color: var(--c-lichen);
		z-index: 1;
		outline: none;

		&:focus-visible {
			outline: 2px solid var(--c-lichen);
			outline-offset: 2px;
		}

		svg {
			size: 2.5rem;
		}
	}
</style>
